<template>
    <div>
        <div class="ppv2-space-y-4">
            <ConfigurationInlineCard
                v-for="card in configurationInlineCards"
                :key="card.id"
                :iconSrc="card.iconSrc"
                :chevronSrc="card.chevronSrc"
                :title="card.title"
                :description="card.description"
                :statusText="card.statusText"
                :statusColor="card.statusColor"
                :class="{ 'ppv2-border-secondary-500 ppv2-border ppv2-bg-secondary-100': card.isActive }"
                @card-action="handleCardAction(card)"
            />
        </div>

        <!-- Dynamic Form Panel -->
        <PanelComponent
            v-if="showPanel && selectedCard && panelComponents[selectedCard.id]"
            :title="selectedCard.title"
            :description="selectedCard.description"
            :cancelText="$t('common.cancel')"
            :validateText="$t('common.save')"
            @close="closePanel"
            @cancel="closePanel(false)"
            @outside-close="closePanel(false)"
            @validate="submitForm"
        >
            <component
                ref="activeForm"
                :is="panelComponents[selectedCard.id]"
                :card="selectedCard"
                :website-setting="websiteSetting"
                :establishment="establishment"
                :countries="countries"
                :currencies="currencies"
                @update="updateEstablishment"
            />
        </PanelComponent>
    </div>
</template>

<script>
import ConfigurationInlineCard from '../../../Shared/ConfigurationInlineCard.vue'
import PanelComponent from '../../../Shared/PanelComponent.vue'
import { EventBus } from '../../../../eventBus.js'

// Form Components
import GeneralInformationForm from './GeneralInformationForm.vue'
import TaxInformationForm from './TaxInformationForm.vue'
import LocationForm from './LocationForm.vue'
import LocationFormOffice from './LocationFormOffice.vue'
import ContactsForm from './ContactsForm.vue'

export default {
    name: 'EstablishmentInfo',
    props: {
        websiteSetting: {
            required: true,
        },
        countries: {
            required: true,
        },
        currencies: {
            required: true,
        },
    },
    components: {
        ConfigurationInlineCard,
        PanelComponent,
        GeneralInformationForm,
        TaxInformationForm,
        LocationForm,
        LocationFormOffice,
        ContactsForm,
    },
    data() {
        return {
            showPanel: false,
            selectedCard: null,
            cards: [
                {
                    id: 1,
                    key: 'general',
                    titleKey: 'configuration.inlineCards.general.title',
                    descriptionKey: 'configuration.inlineCards.general.description',
                    iconSrc: '/v2/images/img_group_1000004856.svg',
                    chevronSrc: '/v2/images/img_chevronright.svg',
                    canShow: true,
                },
                {
                    id: 2,
                    key: 'tax',
                    titleKey: 'configuration.inlineCards.tax.title',
                    descriptionKey: 'configuration.inlineCards.tax.description',
                    iconSrc: '/v2/images/img_vector.svg',
                    chevronSrc: '/v2/images/img_chevronright.svg',
                    canShow: true,
                },
                {
                    id: 3,
                    key: 'location',
                    titleKey: 'configuration.inlineCards.location.title',
                    descriptionKey: 'configuration.inlineCards.location.description',
                    iconSrc: '/v2/images/img_vector_gray_900_01.svg',
                    chevronSrc: '/v2/images/img_chevronright.svg',
                    canShow: true,
                },
                {
                    id: 4,
                    key: 'headOffice',
                    titleKey: 'configuration.inlineCards.headOffice.title',
                    descriptionKey: 'configuration.inlineCards.headOffice.description',
                    iconSrc: '/v2/images/img_group_1000004858.svg',
                    chevronSrc: '/v2/images/img_chevronright.svg',
                    canShow: true,
                },
                {
                    id: 5,
                    key: 'contacts',
                    titleKey: 'configuration.inlineCards.contacts.title',
                    descriptionKey: 'configuration.inlineCards.contacts.description',
                    iconSrc: '/v2/images/img_group_1000004859.svg',
                    chevronSrc: '/v2/images/img_chevronright.svg',
                    canShow: true,
                },
            ],
        }
    },
    computed: {
        establishment() {
            return this.$store.getters['establishment/establishment']
        },
        configurationInlineCards() {
            return this.cards
                .filter((card) => card.canShow)
                .map((card) => ({
                    ...card,
                    title: this.$t(card.titleKey),
                    description: this.$t(card.descriptionKey),
                    isActive: this.selectedCard?.id === card.id,
                }))
        },
        panelComponents() {
            return {
                1: 'GeneralInformationForm',
                2: 'TaxInformationForm',
                3: 'LocationForm',
                4: 'LocationFormOffice',
                5: 'ContactsForm',
            }
        },
    },
    methods: {
        updateCardVisibility(id, canShow) {
            const card = this.cards.find((card) => card.id === id)
            if (card) card.canShow = canShow
        },
        handleCardAction(card) {
            if (this.selectedCard?.id === card.id && this.showPanel) return
            this.selectedCard = { ...card }
            this.showPanel = true
        },
        handleOpenSection(sectionKey) {
            const card = this.cards.find((c) => c.key === sectionKey)
            if (card) {
                this.handleCardAction(card)
            }
        },
        async closePanel(showMessage = true) {
            try {
                const formComponent = this.$refs.activeForm
                if (formComponent?.getFormData) {
                    const formData = formComponent.getFormData()

                    // Merge form data into existing establishment
                    Object.assign(this.establishment, formData)

                    // Send full updated establishment to backend
                    // await this.submitForm(showMessage)
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
            } finally {
                this.showPanel = false
                this.selectedCard = null
            }
        },

        convertToBase64(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader()
                reader.onload = (e) => resolve(e.target.result)
                reader.onerror = reject
                reader.readAsDataURL(file)
            })
        },

        async submitForm(showMessage = true) {
            const formComponent = this.$refs.activeForm
            if (formComponent?.validateForm && !formComponent.validateForm()) {
                this.$toast.error(this.$t('common.fill_required_fields'))
                return
            }

            const websiteSettingFiltered = this.$store.getters['establishment/websiteSetting'].filter((item) => item.parameter)

            const appSettingFiltered = this.$store.getters['establishment/appSetting']

            const establishment = { ...this.$store.getters['establishment/establishment'] }

            if (establishment.map_coords && typeof establishment.map_coords === 'string') {
                try {
                    establishment.map_coords = JSON.parse(establishment.map_coords)
                } catch (error) {
                    this.$toast.error(error.response.data.message)
                    console.warn('Invalid JSON in map_coords:', establishment.map_coords)
                    establishment.map_coords = null
                }
            }

            const fieldsToConvert = [
                { source: 'logo', target: 'logo_base64', label: 'logo' },
                { source: 'photo', target: 'photo_base64', label: 'bannière' },
            ]

            for (const field of fieldsToConvert) {
                if (establishment[field.source] instanceof File) {
                    establishment[field.target] = await this.convertToBase64(establishment[field.source])
                    delete establishment[field.source]
                }
            }

            try {
                const payload = {
                    establishment: establishment,
                    websiteSetting: websiteSettingFiltered,
                    appSetting: appSettingFiltered,
                }

                if (this.selectedCard.key === 'PaymentMethods') {
                    payload.selectedPaymentMethods = this.$store.getters['establishment/paymentMethodsSelectedIds']
                }

                const data = await this.$store.dispatch('establishment/updateEstablishment', payload)

                // Apply locale only after successful save
                const savedLanguage = establishment.language
                if (savedLanguage && ['en', 'fr'].includes(savedLanguage)) {
                    this.$i18n.locale = savedLanguage
                    localStorage.setItem('locale', savedLanguage)
                }

                if (showMessage) {
                    this.$toast.success(this.$t('common.config_updated_success'))
                }
                EventBus.$emit('establishment-settings-updated')

                this.showPanel = false
            } catch (error) {
                this.$toast.error(error.response.data.message)
                this.showErrors(error)
            }
        },

        updateEstablishment(updatedData) {
            let isSameBillingAddress = updatedData?.is_same_billing_address ?? true
            if (this.selectedCard?.id === 3) {
                this.updateCardVisibility(4, !isSameBillingAddress)
            }
            Object.assign(this.establishment, updatedData)
        },

        showErrors(response) {
            if (response && response.response && response.response.data && response.response.data.errors) {
                const errors = response.response.data.errors

                for (const key in errors) {
                    if (errors.hasOwnProperty(key)) {
                        const errorMessage = errors[key][0]
                        this.showErrorNotification(errorMessage)
                    }
                }
            } else if (response && response.response && response.response.data && response.response.data.message) {
                this.showErrorNotification(response.response.data.message)
            } else {
                this.showErrorNotification('An error occurred. Please try again.')
            }
        },

        showErrorNotification(message) {
            this.$toast.error(message)
        },
    },
    created() {
        const isSameBillingAddress = this.establishment?.is_same_billing_address ?? true
        this.updateCardVisibility(4, !isSameBillingAddress)

        EventBus.$on('open-establishment-section', this.handleOpenSection)

        // Also check if we should open a section on load
        if (window.location.hash.includes(':')) {
            const subHash = window.location.hash.split(':')[1]
            this.$nextTick(() => {
                this.handleOpenSection(subHash)
            })
        }
    },
    beforeDestroy() {
        EventBus.$off('open-establishment-section', this.handleOpenSection)
    },
}
</script>
